import { useMemo } from "react";

import { observer } from "mobx-react-lite";
import Link from "next/link";

import { useStore } from "@/store";
import { docPageUrl } from "@/urls";
import { extractSnippetWithStrong } from "@/utils/sanitizeHTML";
import { twMerge } from "@/utils/twMerge";

import type { DocumentSearchResult } from "@/api";

export const SearchResult = observer(function SearchResult({
  searchResult,
}: {
  searchResult: DocumentSearchResult;
}) {
  const { documentTree } = useStore();
  const isActive = useMemo(() => {
    return documentTree.currentDoc?.uid === searchResult.uid;
  }, [documentTree.currentDoc, searchResult]);
  const { snippet: bodySnippet } = extractSnippetWithStrong(searchResult.searchDoc || "", 65);

  let content = null;

  if (searchResult.title) {
    // eslint-disable-next-line react/no-danger
    content = <span dangerouslySetInnerHTML={{ __html: searchResult.title }} />;
  } else {
    content = <span>{searchResult.uid}</span>;
  }

  return (
    <div
      className={twMerge(
        "relative flex group rounded-6 cursor-pointer select-none",
        "hover:bg-sidebar-button-hover-inverted-bg",
        isActive && "border-blue-100 dark:border-blue-900 bg-blue-100 dark:bg-blue-900",
      )}
    >
      <Link
        href={docPageUrl({ docId: searchResult.uid })}
        className={twMerge(
          "flex flex-col justify-center w-full min-h-7.5 px-5 py-1.5",
          "sm:pr-10 sm:py-[9px]",
        )}
      >
        <span
          className={twMerge(
            "text-size-15 leading-[18px] tracking-tight text-left",
            "sm:text-[17px] sm:leading-[20px]",
          )}
        >
          {content}
        </span>
        {bodySnippet && bodySnippet !== searchResult.title && (
          <span
            className={twMerge(
              "text-size-14 leading-[16px] tracking-tight text-left text-gray-500",
              "sm:text-size-13 sm:leading-[16px]",
              "[&>strong]:text-document-text",
            )}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: bodySnippet }}
          />
        )}
      </Link>
    </div>
  );
});
